import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useGeo } from "@/context/GeoContext";
import { BookOpen, ExternalLink, Loader2 } from "lucide-react";

/**
 * Affiliate Librarian — surfaces 2 contextual book CTAs at the end of a
 * path / module. Books come from the cached /api/books/recommend endpoint
 * (Gemini Flash + 30-day cache) and links carry the digitalxcons-21
 * Amazon affiliate tag. Locale auto-resolves to .in for India, .com else.
 */
export default function AffiliateLibrarian({ goal, keywords }) {
  const geo = useGeo();
  const [state, setState] = useState({ loading: true, books: [], error: null });

  useEffect(() => {
    if (!goal || goal.trim().length < 3) {
      setState({ loading: false, books: [], error: null });
      return;
    }
    let cancelled = false;
    setState({ loading: true, books: [], error: null });
    api.post("/books/recommend", {
      goal: goal.trim(),
      keywords: Array.isArray(keywords) ? keywords.slice(0, 8) : [],
      locale: geo?.is_india ? "in" : "us",
    })
      .then(({ data }) => {
        if (!cancelled) setState({ loading: false, books: data?.books || [], error: null });
      })
      .catch((e) => {
        if (!cancelled) setState({ loading: false, books: [], error: e?.response?.data?.detail || "Could not load recommendations" });
      });
    return () => { cancelled = true; };
  }, [goal, JSON.stringify(keywords), geo?.is_india]);

  if (state.loading) {
    return (
      <section className="glass p-5 rounded-2xl border border-white/10" data-testid="affiliate-librarian-loading">
        <div className="flex items-center gap-2 text-white/55 text-sm">
          <Loader2 size={14} className="animate-spin" /> Curating book picks for this topic…
        </div>
      </section>
    );
  }

  if (state.error || state.books.length === 0) {
    return null;
  }

  const trackClick = (book) => {
    // sendBeacon survives the navigation away from our page — the standard
    // anchor click would otherwise race the browser's tab-jump. axios is
    // unsafe here because the request would be cancelled mid-flight when
    // the new tab opens. Beacon is fire-and-forget and queued by the OS.
    try {
      const apiBase = (process.env.REACT_APP_BACKEND_URL || "") + "/api/affiliate/click";
      const payload = JSON.stringify({
        url: book.amazon_url,
        title: (book.title || "").slice(0, 200),
        source: "librarian",
        goal: (goal || "").slice(0, 200),
      });
      const blob = new Blob([payload], { type: "application/json" });
      navigator.sendBeacon?.(apiBase, blob);
    } catch {
      /* tracking is best-effort, never break the navigation */
    }
  };

  return (
    <section
      className="glass p-5 md:p-6 rounded-2xl border border-[#ffb067]/25"
      data-testid="affiliate-librarian"
    >
      <div className="flex items-center gap-2 mb-4">
        <BookOpen size={14} className="text-[#ffb067]" />
        <span className="label-eyebrow !text-[10px] text-[#ffb067]/85">Recommended Reading</span>
        <span className="text-[10px] text-white/40 ml-auto" title="OAK earns a small commission if you buy via these links">
          Affiliate links · supports OAK
        </span>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="affiliate-books-grid">
        {state.books.slice(0, 2).map((b, i) => (
          <a
            key={i}
            href={b.amazon_url}
            target="_blank"
            rel="noreferrer noopener sponsored"
            onClick={() => trackClick(b)}
            className="rounded-xl border border-white/10 bg-white/[0.03] hover:bg-white/[0.06] hover:border-[#ffb067]/40 transition p-4 flex flex-col gap-2 group"
            data-testid={`affiliate-book-${i}`}
          >
            <div className="flex items-start justify-between gap-2">
              <div className="font-display font-semibold text-white text-base leading-tight">
                {b.title}
              </div>
              <ExternalLink size={13} className="text-white/40 group-hover:text-[#ffb067] transition shrink-0 mt-1" />
            </div>
            {b.author && <div className="text-[12px] text-white/55">by {b.author}</div>}
            {b.why && <div className="text-[12px] text-white/65 leading-relaxed">{b.why}</div>}
            <div className="text-[10px] text-[#ffb067]/80 font-mono-oak mt-auto pt-1">
              View on Amazon →
            </div>
          </a>
        ))}
      </div>
    </section>
  );
}
